import React, { useEffect } from "react";
import {
  getEmailPermissions,
  updateEmailPermissions,
} from "adminApi/EmailSettingApi/EmaillSetting";
import { validateForm } from "utils/validation";
import { EmailSettingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EmailSettingForm from "./Componate/EmailSettingsForm";
import fields from "../json/EmailSettingFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import { useRoleVerify } from "Common/RoleVerify";

/**
 * EmailSetting Component
 * Manages email notification permissions (e.g., booking, check-in, etc.)
 */
const EmailSetting = () => {
  // Check if user has permission to edit settings
  const canUpdate = useRoleVerify("", ["can_edit"]);
  const { EmailSettingFormData } = fields;

  // Initialize form state with empty values based on JSON field definitions
  const initialValues = EmailSettingFormData.reduce(
    (acc, field) => ({ ...acc, [field.name]: "0" }),
    {}
  );

  const { values, errors, handleChange, setValues, setErrors } = useFormChange(
    initialValues,
    EmailSettingRules
  );

  // Fetch all existing permissions on mount
  const { data: emailPermissions, loading } = useFetchAllData(getEmailPermissions);

  // Synchronize component state with fetched data
  useEffect(() => {
    if (emailPermissions && Array.isArray(emailPermissions)) {
      const permissionValues = emailPermissions.reduce((acc, opt) => {
        acc[opt.permission] = opt.status ?? "0";
        return acc;
      }, {});
      setValues((prev) => ({ ...prev, ...permissionValues }));
    }
  }, [emailPermissions, setValues]);

  // Hook for handling form submission
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      // Production refinement: Sending clean JSON object instead of FormData
      return await updateEmailPermissions({ data: formValues });
    },
    (vals) => {
      // Perform frontend validation before submission
      const validationErrors = validateForm(vals, EmailSettingRules);
      setErrors(validationErrors);
      return validationErrors;
    },
    () => {
      // Success callback
    },
    "Email permissions updated successfully!"
  );

  return (
    <>
      {/* Show spinner during data fetch or submission */}
      {(loading || loadingSubmit) && <Spinner isSpinner={true} />}
      
      <EmailSettingForm
        fields={EmailSettingFormData}
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        title="Email Trigger Settings"
        canUpdate={canUpdate}
      />
    </>
  );
};

export default EmailSetting;
